'use client';

import { Container } from '@/components/layout/container/container';
import { Image } from '@/components/ui/image/image';
import { cn } from '@/lib/utils';
import { ArrowLeft, Menu, X } from 'lucide-react';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Link from 'next/link';
import { HTMLAttributes, useState } from 'react';
import { DesktopNav } from './desktopNavigation/DesktopNav';
import styles from './header.module.scss';
import { MobileNav } from './mobileNavigation/MobileNav';

import type { CtaType, FooterLinkType, NestedMenuLink } from './types';

export type HeaderProps = {
  /** URL or StaticImport for logo image */
  logo?: string | StaticImport | null;
  /** Descriptive alt text for the logo */
  logoAlt?: string | null;
  /** Nested menu links structure */
  menuLinks: NestedMenuLink[];
  // Links alignment
  linksAlignment?: 'Left' | 'Right';
  /** Call-to-action buttons array - accepts Contentful Entry array or compatible structure */
  ctas?: CtaType[];
  /** Footer links for mobile menu - accepts Contentful Entry structure or compatible structure */
  footerLinks?: FooterLinkType[] | null;
};

export const Header = ({
  logo,
  logoAlt,
  menuLinks,
  ctas,
  footerLinks,
  className,
  linksAlignment = 'Right',
  ...props
}: HeaderProps & HTMLAttributes<HTMLElement>) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [navigateBack, setNavigateBack] = useState<(() => void) | null>(null);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleBackClick = () => {
    if (navigateBack) {
      navigateBack();
    }
  };

  return (
    <header className={cn(styles.component, className)} {...props}>
      <Container className={styles.component_container}>
        <div
          className={cn(
            styles.component_navWrapper,
            linksAlignment === 'Left' && styles.component_navWrapper_left,
          )}
        >
          {showBackButton ? (
            <button
              type="button"
              onClick={handleBackClick}
              className={styles.component_backButton}
              aria-label="Go back"
            >
              <ArrowLeft width={18} height={23} />
              <span>Back</span>
            </button>
          ) : (
            <div className={styles.component_logo}>
              {logo ? (
                <Link href="/">
                  <Image
                    src={logo}
                    width={150}
                    height={39}
                    alt={logoAlt ?? 'Once For All'}
                    priority
                  />
                </Link>
              ) : (
                <span className={styles.component_logoText}>Once For All</span>
              )}
            </div>
          )}
          <div className={styles.component_menuLinks}>
            <DesktopNav menuLinks={menuLinks} ctas={[]} />
          </div>
        </div>

        <div className={styles.component_rightSection}>
          {ctas && ctas.length > 0 && (
            <div className={styles.component_ctas}>
              <DesktopNav menuLinks={[]} ctas={ctas} />
            </div>
          )}

          <button
            className={styles.component_hamburger}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </Container>

      <MobileNav
        menuLinks={menuLinks}
        ctas={ctas}
        footerLinks={footerLinks ?? undefined}
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        setShowBackButton={setShowBackButton}
        setNavigateBack={setNavigateBack}
      />
    </header>
  );
};
